import React from 'react';
import { MapPin, Clock, Navigation } from 'lucide-react';

interface VenueNeonSectionProps {
  venue: {
    dateStr: string;
    dateMr: string;
    time: string;
    venue: string;
    venueMr: string;
  };
  language: 'en' | 'mr';
}

export const VenueNeonSection: React.FC<VenueNeonSectionProps> = ({ venue, language }) => {
  const openMap = () => {
    const mapsUrl = `https://www.google.com/maps/search/${encodeURIComponent(venue.venue)}`;
    window.open(mapsUrl, '_blank');
  };

  const venueName = language === 'mr' ? venue.venueMr : venue.venue;
  const dateStr = language === 'mr' ? venue.dateMr : venue.dateStr;

  return (
    <section className="relative py-24 px-4">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-center text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400 mb-12">
          {language === 'mr' ? 'पार्टी कुठे आहे' : 'Party Details'}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Date Card */}
          <div className="group relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-xl blur opacity-75 group-hover:opacity-100 transition-all duration-300" />
            <div className="relative bg-slate-900 rounded-xl p-8 text-center border border-slate-800">
              <div className="text-4xl mb-4">📅</div>
              <p className="text-gray-400 text-sm uppercase tracking-wider mb-2">
                {language === 'mr' ? 'तारीख' : 'Date'}
              </p>
              <p className="text-xl font-bold text-cyan-300">
                {dateStr}
              </p>
            </div>
          </div>

          {/* Time Card */}
          <div className="group relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl blur opacity-75 group-hover:opacity-100 transition-all duration-300" />
            <div className="relative bg-slate-900 rounded-xl p-8 text-center border border-slate-800">
              <div className="text-4xl mb-4">🕐</div>
              <p className="text-gray-400 text-sm uppercase tracking-wider mb-2">
                {language === 'mr' ? 'वेळ' : 'Time'}
              </p>
              <p className="text-xl font-bold text-pink-300">
                {venue.time}
              </p>
            </div>
          </div>

          {/* Venue Card */}
          <div className="group relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-500 to-cyan-500 rounded-xl blur opacity-75 group-hover:opacity-100 transition-all duration-300" />
            <div className="relative bg-slate-900 rounded-xl p-8 text-center border border-slate-800">
              <div className="text-4xl mb-4">📍</div>
              <p className="text-gray-400 text-sm uppercase tracking-wider mb-2">
                {language === 'mr' ? 'स्थान' : 'Location'}
              </p>
              <p className="text-lg font-bold text-purple-300 line-clamp-2">
                {venueName}
              </p>
            </div>
          </div>
        </div>

        {/* Map Button */}
        <div className="mt-12 flex justify-center">
          <button
            onClick={openMap}
            className="group px-8 py-4 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white font-bold rounded-full hover:shadow-2xl hover:shadow-cyan-500/50 transition-all transform hover:scale-110"
          >
            <span className="flex items-center gap-3">
              <Navigation size={20} />
              {language === 'mr' ? 'नकाशा पहा' : 'View on Map'}
            </span>
          </button>
        </div>

        {/* Info Section */}
        <div className="mt-16 bg-gradient-to-r from-slate-900/50 to-slate-800/50 border border-slate-700 rounded-xl p-8">
          <p className="text-gray-300 text-center text-lg leading-relaxed">
            {language === 'mr'
              ? '🎉 आमच्या प्रिय पार्टीमध्ये सामील व्हा! आपल्या उपस्थितीचा प्रतीक्षा करत आहोत.'
              : "🎉 Join us for an unforgettable celebration! We can't wait to party with you."}
          </p>
        </div>
      </div>
    </section>
  );
};
